/**
 * API MAPPERS
 *
 * Shared mapping functions for converting API responses (snake_case) to
 * frontend types (camelCase). Extracted from worldStore for reuse in DataSource.
 */

import {
  normalizePipelineStage,
  type Project,
  type ProjectStatus,
  type Zone,
  type Pipeline,
  type PipelineDirection,
  type Agent,
  type AgentState,
  type Task,
} from "@/stores/worldStore";
import type { Workflow } from "@/stores/workflowStore";

const MAX_WORKFLOW_TIMEOUT_SECONDS = 7200;
/** Legacy configs stored timeouts in ms; values >= this threshold are auto-converted to seconds. */
const MS_THRESHOLD = 10_000;
const DEFAULT_ZONE_COLOR = "#3b82f6";

function safeJsonParse(s: string): unknown {
  try { return JSON.parse(s); } catch { return undefined; }
}

function normalizeTimeoutValue(raw: unknown): number | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === "boolean") return undefined;

  const parsed =
    typeof raw === "number"
      ? raw
      : typeof raw === "string"
      ? Number.parseInt(raw, 10)
      : NaN;

  if (!Number.isFinite(parsed)) return undefined;

  let value = Math.floor(parsed);
  if (value <= 0) return 0;

  // Legacy workflow configs sometimes persisted timeout values in milliseconds.
  if (value >= MS_THRESHOLD) {
    value = Math.floor(value / 1000);
  }

  return Math.min(value, MAX_WORKFLOW_TIMEOUT_SECONDS);
}

function normalizeWorkflowNodeConfig(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const config = { ...(raw as Record<string, unknown>) };

  const timeout = normalizeTimeoutValue(config.timeout);
  if (timeout !== undefined) config.timeout = timeout;

  const stallTimeout = normalizeTimeoutValue(config.stallTimeout);
  if (stallTimeout !== undefined) config.stallTimeout = stallTimeout;

  return config;
}

export function mapProjectFromApi(p: any): Project {
  const rawConfig = p?.project_config ?? p?.projectConfig;
  return {
    id: p?.id ?? p?.project_id ?? p?.projectId ?? 0,
    name: p?.name ?? "",
    status: (p?.status as ProjectStatus) ?? "healthy",
    x: p?.x ?? 0,
    y: p?.y ?? 0,
    width: p?.width ?? p?.project_width,
    height: p?.height ?? p?.project_height,
    zoneId: p?.zone_id ?? p?.zoneId,
    description: p?.description ?? "",
    lastDeployed: p?.last_deployed ?? p?.lastDeployed,
    githubRepo: p?.github_repo ?? p?.githubRepo,
    fargateCluster: p?.fargate_cluster ?? p?.fargateCluster,
    containerCount: p?.container_count ?? p?.containerCount,
    shape: p?.shape,
    icon: p?.icon,
    customShapeImage: p?.custom_shape_image ?? p?.customShapeImage,
    workspaceId: p?.workspace_id ?? p?.workspaceId,
    subdirectory: p?.subdirectory,
    monorepoGroup: p?.monorepo_group ?? p?.monorepoGroup,
    projectConfig: typeof rawConfig === "string" ? safeJsonParse(rawConfig) : rawConfig,
    project_category: p?.project_category ?? p?.projectCategory ?? 'coding',
    project_subtype: p?.project_subtype ?? p?.projectSubtype ?? null,
  };
}

export function mapZoneFromApi(z: any): Zone {
  return {
    id: z?.id ?? 0,
    name: z?.name ?? "",
    color: z?.color ?? DEFAULT_ZONE_COLOR,
    startX: z?.start_x ?? z?.startX ?? 0,
    startY: z?.start_y ?? z?.startY ?? 0,
    width: z?.width ?? 1,
    height: z?.height ?? 1,
    isEditable: z?.isEditable ?? z?.is_editable,
    workspaceId: z?.workspace_id ?? z?.workspaceId,
  };
}

export function mapPipelineFromApi(p: any): Pipeline {
  return {
    id: p?.id ?? `${p?.from_id ?? p?.fromId ?? "unknown"}-${p?.to_id ?? p?.toId ?? "unknown"}`,
    fromId: p?.from_id ?? p?.fromId ?? 0,
    toId: p?.to_id ?? p?.toId ?? 0,
    fromType: p?.from_type ?? p?.fromType ?? "project",
    toType: p?.to_type ?? p?.toType ?? "project",
    direction: (p?.direction as PipelineDirection) ?? "forward",
    style: p?.style ?? p?.pipeline_style,
    traffic: p?.traffic ?? p?.pipeline_traffic,
    color: p?.color,
    label: p?.label,
    animated: p?.animated ?? false,
  };
}

export function mapAgentFromApi(a: any): Agent {
  return {
    id: a?.id,
    projectId: a?.project_id ?? a?.projectId,
    state: (a?.state as AgentState) ?? "idle",
    name: a?.name,
    taskDescription: a?.task_description ?? a?.taskDescription,
    taskId: a?.task_id ?? a?.taskId,
    pipelineStage: normalizePipelineStage(a?.pipeline_stage ?? a?.pipelineStage),
    config: a?.config ?? {},
    currentPrompt: a?.current_prompt ?? a?.currentPrompt,
    startedAt: a?.started_at ?? a?.startedAt,
    estimatedCompletion: a?.estimated_completion ?? a?.estimatedCompletion,
    attentionState: a?.attention_state ?? a?.attentionState,
    attentionMessage: a?.attention_message ?? a?.attentionMessage,
    attentionDetails: a?.attention_details ?? a?.attentionDetails,
  };
}

export function mapTaskFromApi(t: any): Task {
  return {
    id: t?.id,
    projectId: t?.project_id ?? t?.projectId,
    title: t?.title ?? "",
    description: t?.description ?? "",
    metadata: t?.metadata,
    githubIssueUrl: t?.github_issue_url ?? t?.githubIssueUrl,
    pipelineStage: normalizePipelineStage(t?.pipeline_stage ?? t?.pipelineStage),
    status: (t?.status as Task["status"]) ?? "pending",
    assignedAgentId: t?.assigned_agent_id ?? t?.assignedAgentId,
    issueType: (t?.issue_type as Task["issueType"]) ?? t?.issueType ?? "feature",
    workflowId: t?.workflow_id ?? t?.workflowId,
    workflowRunId: t?.workflow_run_id ?? t?.workflowRunId,
    workflowStatus: t?.workflow_status ?? t?.workflowStatus,
    humanReviewRequired: t?.human_review_required ?? t?.humanReviewRequired,
    baseBranch: t?.base_branch ?? t?.baseBranch,
    priority: t?.priority,
    complexity: t?.complexity,
    impact: t?.impact,
  };
}

/** Convert API response (graph-wrapped) to flat Workflow format */
export function apiToWorkflow(raw: any): Workflow {
  const graph = raw.graph || {};
  return {
    id: raw.id,
    name: raw.name,
    description: raw.description || "",
    issueType: raw.issue_type || raw.issueType,
    nodes: (graph.nodes || raw.nodes || []).map((n: any) => ({
      id: n.id,
      type: n.type,
      label: n.label || n.type,
      x: n.x || 0,
      y: n.y || 0,
      config: normalizeWorkflowNodeConfig(n.config || {}),
    })),
    connections: (graph.connections || raw.connections || []).map((c: any) => ({
      id: c.id,
      sourceId: c.sourceId || c.source,
      targetId: c.targetId || c.target,
      condition: c.condition,
    })),
    isDefault: raw.is_default ?? raw.isDefault ?? false,
    workspaceId: raw.workspace_id || raw.workspaceId,
    version: raw.version,
    createdAt: raw.created_at || raw.createdAt || new Date().toISOString(),
    updatedAt: raw.updated_at || raw.updatedAt || new Date().toISOString(),
    stageLabelMap: raw.stage_label_map || raw.stageLabelMap,
    stageActions: raw.stage_actions || raw.stageActions,
  };
}

/** Convert flat Workflow to API format (graph-wrapped) */
export function workflowToApi(wf: Workflow): any {
  return {
    name: wf.name,
    issue_type: wf.issueType,
    is_default: wf.isDefault,
    graph: {
      nodes: wf.nodes.map((n) => ({
        id: n.id,
        type: n.type,
        label: n.label,
        x: n.x,
        y: n.y,
        config: normalizeWorkflowNodeConfig(n.config),
      })),
      connections: wf.connections.map((c) => ({
        id: c.id,
        sourceId: c.sourceId,
        targetId: c.targetId,
        condition: c.condition,
      })),
    },
  };
}
